import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, $getRoot, $isElementNode, LexicalNode } from 'lexical';
import { useEffect, useRef } from 'react';

import {
  $isListItemNode,
  $isListNode,
  getRootListAndLevel,
  PhxListNode,
  type PhxListVariant,
} from '../../nodes/PhxListNode';

/**
 * When Lexical restructures lists (e.g. on indent/outdent), new root list nodes
 * can be created without our __phxVariant. This plugin keeps the last-seen root
 * list variant and assigns it to any newly created root list that has no variant,
 * so data-list-type (e.g. legacy_alpha) is preserved.
 */
export default function PhxListVariantPropagationPlugin(): null {
  const [editor] = useLexicalComposerContext();
  const lastKnownRootVariantRef = useRef<PhxListVariant | null>(null);
  const lastKnownRootListStyleTypeRef = useRef<string | null>(null);

  useEffect(() => {
    const unregisterUpdate = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        const walk = (node: LexicalNode) => {
          if ($isListNode(node)) {
            const listNode = node as PhxListNode;
            const { level } = getRootListAndLevel(listNode);
            if (level === 0) {
              // Only store when the node has an explicit variant so we don't
              // overwrite with the default 'custom' after an indent (update
              // listener can run and see the new root before we fix it).
              const explicitVariant = (listNode as any).__phxVariant;
              const explicitListStyleType = (listNode as any).__phxListStyleType;
              if (explicitVariant !== undefined && explicitVariant !== null) {
                lastKnownRootVariantRef.current = explicitVariant;
                lastKnownRootListStyleTypeRef.current =
                  explicitListStyleType !== undefined ? explicitListStyleType : null;
              }
            }
          }
          if ($isElementNode(node)) {
            node.getChildren().forEach(walk);
          }
        };
        root.getChildren().forEach(walk);
      });
    });

    const unregisterMutation = editor.registerMutationListener(PhxListNode, (mutations) => {
      const keysToFix = Array.from(mutations.entries())
        .filter(([, type]) => type === 'created' || type === 'updated')
        .map(([key]) => key);

      if (keysToFix.length === 0) return;

      editor.update(() => {
        const variantToApply = lastKnownRootVariantRef.current ?? 'custom';
        const listStyleTypeToApply = lastKnownRootListStyleTypeRef.current ?? null;

        for (const key of keysToFix) {
          const node = $getNodeByKey(key);
          if (!node || !$isListNode(node)) continue;

          const listNode = node as PhxListNode;
          const { root, level } = getRootListAndLevel(listNode);

          if (level !== 0) continue;

          const hasExplicitVariant = (root as any).__phxVariant !== undefined
            && (root as any).__phxVariant !== null;
          if (hasExplicitVariant) continue;

          const writable = root.getWritable();

          // Preserve any existing custom list-style-type when propagating variants so that
          // inline list markers (e.g. upper-roman) are not lost. If there is no existing
          // style on this root, fall back to the last-known root style we observed.
          const existingStyle = (root as any).__phxListStyleType;
          const styleToSet =
            existingStyle !== undefined && existingStyle !== null
              ? existingStyle
              : listStyleTypeToApply;

          if (styleToSet !== null) {
            (writable as any).__phxListStyleType = styleToSet;
          }

          if (typeof (writable as PhxListNode).setPhxVariant === 'function') {
            (writable as PhxListNode).setPhxVariant!(variantToApply);
          } else {
            (writable as any).__phxVariant = variantToApply;
          }

          // Force nested list nodes to refresh their DOM so they get the correct
          // level and marker (e.g. "1" not "a" for legacy_alpha level 1). They may
          // have been created before attachment so createDOM saw level 0.
          const refreshNestedLists = (list: PhxListNode) => {
            list.getChildren().forEach((child) => {
              if ($isListItemNode(child) && $isElementNode(child)) {
                child.getChildren().forEach((grandchild) => {
                  if ($isListNode(grandchild)) {
                    (grandchild as PhxListNode).getWritable();
                    refreshNestedLists(grandchild as PhxListNode);
                  }
                });
              }
            });
          };
          refreshNestedLists(writable as PhxListNode);
        }
      });
    });

    return () => {
      unregisterUpdate();
      unregisterMutation();
    };
  }, [editor]);

  return null;
}
